import React, { MouseEvent, ReactElement, useState } from 'react';
import { TableRow, TableCell, ListItemSecondaryAction } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import cx from 'classnames';

import { Row as IRow, CellTypes, Cell } from '../DataTable.types';
import { makeStyles } from '~/UIKit/Style';
import { IconButton, Menu, MenuItem } from '~/UIKit';
import { GRAY_ACTIVE_SELECTION } from '~/Constants/Colors.constants';
import { FONT_SIZE_INBETWEEN } from '~/Constants/UI.constants';

const useStyles = (selectableRows = false, active = false) =>
  makeStyles(() => ({
    row: {
      cursor: selectableRows ? 'pointer' : 'inherit',
      height: '60px',
      letterSpacing: 0.5,
      lineHeight: '16px',
      backgroundColor: active ? GRAY_ACTIVE_SELECTION : 'white',
    },
    topRowExpanded: {
      border: 'none',
    },
    rowCell: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '16px',
      fontSize: FONT_SIZE_INBETWEEN,
      '&:first-of-type': {
        fontWeight: 'bold',
      },
    },
    // Expansion animation
    expandableRow: {
      flex: '1 0 100%',
      height: 0,
      transition: 'height 0.1s',
    },
    expandableRowExpanded: {
      transition: 'height 0.2s',
      height: '60px',
    },
    expandableCellHidden: {
      paddingTop: 0,
      paddingBottom: 0,
      border: 'none',
    },
    expandedRowContentHidden: {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      overflow: 'hidden',
      transition: 'height 0.1s',
    },
    expandedRowContentShown: {
      height: '30px',
      transition: 'height 0.2s',
      overflow: 'hidden',
    },
    actionContainer: {
      position: 'relative',
    },
    listItemSecondaryAction: {
      height: 30,
    },
    listItemMenuButton: {
      padding: 3,
    },
    menuItemRow: {
      fontSize: FONT_SIZE_INBETWEEN,
    },
  }));

interface RowProps {
  testId?: string | number;
  row: IRow;
  isExpandable?: boolean;
  getExpandedRow?: (row: IRow) => ReactElement;
  getCellForType: (type: CellTypes) => (props: any) => JSX.Element | null;
  onRowClicked?: (row: any) => void;
  useSelectableRows?: boolean;
  selectRow: (cellValue: any) => void;
}

const useRow = ({
  onRowClicked,
  isExpandable,
  row,
}: {
  onRowClicked?: (row: any) => void;
  isExpandable?: boolean;
  row: IRow;
}) => {
  const [expanded, setExpanded] = useState(false);
  const handleClick = () => {
    onRowClicked && onRowClicked(row.rowData);
    isExpandable && setExpanded((oldValue) => !oldValue);
  };

  return {
    handleClick,
    expanded,
  };
};

export const Row = ({
  row,
  isExpandable,
  getExpandedRow,
  testId,
  onRowClicked,
  getCellForType,
  selectRow,
  useSelectableRows,
}: RowProps) => {
  const { classes } = useStyles(useSelectableRows || isExpandable, row.rowData?.active)();
  const { handleClick, expanded } = useRow({ isExpandable, row, onRowClicked });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotClickableCell = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  return (
    <>
      <TableRow className={classes.row} data-testid={`tablerow-${testId}`} onClick={handleClick}>
        {row.cells.map((cell: Cell, idx: number) => {
          const CurrentCell = getCellForType(cell.column.cellType);
          if (cell.column.cellType === CellTypes.Checkbox) {
            return (
              <CurrentCell
                formatter={cell.column.formatter}
                handleCheckedChange={selectRow}
                key={idx}
                value={cell.value}
                className={cx({
                  [classes.rowCell]: true,
                  [classes.topRowExpanded]: expanded,
                })}
              />
            );
          }
          return (
            <CurrentCell
              formatter={cell.column.formatter}
              key={idx}
              value={cell.value}
              style={cell.column.style}
              className={cx({
                [classes.rowCell]: true,
                [classes.topRowExpanded]: expanded,
              })}
            />
          );
        })}
        {row.rowData?.actions?.length > 0 && (
          <TableCell className={classes.actionContainer} onClick={handleNotClickableCell}>
            <ListItemSecondaryAction
              data-testid="FitoListItemSecondaryAction"
              className={classes.listItemSecondaryAction}
            >
              <IconButton
                aria-controls="list-actions-menu"
                aria-haspopup
                onClick={handleMenuOpen}
                className={classes.listItemMenuButton}
                size="large"
              >
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                id="list-actions-menu"
                keepMounted
                onClose={handleClose}
                open={Boolean(anchorEl)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {row.rowData.actions.map((action: any) => (
                  <MenuItem
                    key={`${action.name}`}
                    onClick={() => {
                      action.onClick(row.rowData);
                      handleClose();
                    }}
                    className={classes.menuItemRow}
                  >
                    {action.name}
                  </MenuItem>
                ))}
              </Menu>
            </ListItemSecondaryAction>
          </TableCell>
        )}
      </TableRow>
      {isExpandable && !!row.rowData && !!getExpandedRow && (
        <TableRow
          className={cx(classes.row, classes.expandableRow, {
            [classes.expandableRowExpanded]: expanded,
          })}
        >
          <TableCell
            className={cx({
              [classes.expandableCellHidden]: !expanded,
            })}
            colSpan={row.cells?.length}
          >
            <div
              className={cx({
                [classes.expandedRowContentHidden]: !expanded,
                [classes.expandedRowContentShown]: expanded,
              })}
            >
              {getExpandedRow(row.rowData)}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
